import React, { useEffect, useRef, useState } from 'react';

import {
  faChevronLeft,
  faChevronRight,
  faGripVertical,
  faSpinner,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/index';
import { Input } from '@repo/videa-components/src/ui/input';
import { Textarea } from '@repo/videa-components/src/ui/textarea';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHistory, useParams } from 'react-router-dom';

import { disableKeyPressAtom } from '@/modules/annotator/casePresentation/atoms';
import type { ImageQualityAssessmentResponse } from '@/react-query/useImageQualityAssessment';
import { QualityIssueType } from '@/react-query/useImageQualityAssessment';

import routes from '../../../routes/routes.constants';

import {
  imageQualityAssessmentCurrentIndexAtom,
  imageQualityAssessmentOverlayPositionAtom,
  imageQualityAssessmentPatientIdsAtom,
} from './imageToolbar/atoms';

export interface ImageQualityAssessmentOverlayProps {
  imageId?: string;
  onSubmit: (data: {
    rating: number;
    issueTypes: QualityIssueType[];
    note: string;
  }) => void;
  onSubmitAndNext: (data: {
    rating: number;
    issueTypes: QualityIssueType[];
    note: string;
  }) => void;
  onClose: () => void;
  existingAssessment?: ImageQualityAssessmentResponse | null;
  isOpen: boolean;
  hasNextImage?: boolean;
  isSubmitting?: boolean;
}

export const ImageQualityAssessmentOverlay: React.FC<
  ImageQualityAssessmentOverlayProps
> = ({
  onSubmit,
  onSubmitAndNext,
  onClose,
  existingAssessment,
  isOpen,
  hasNextImage = true,
  isSubmitting = false,
}) => {
  // Route Params
  const { id: currentPatientId } = useParams<{ id: string }>();

  // State Variables
  const [rating, setRating] = useState<number | null>(
    existingAssessment?.qualityRating || null,
  );
  const [selectedIssueTypes, setSelectedIssueTypes] = useState<
    QualityIssueType[]
  >(existingAssessment?.qualityIssueTypes || []);
  const [note, setNote] = useState(existingAssessment?.note || '');
  const [position, setPosition] = useAtom(
    imageQualityAssessmentOverlayPositionAtom,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [currentIndex, setCurrentIndex] = useAtom(
    imageQualityAssessmentCurrentIndexAtom,
  );
  const [patientInputValue, setPatientInputValue] = useState(
    currentIndex !== null ? String(currentIndex + 1) : '',
  );

  // Custom Hooks
  const { t } = useLingui();
  const history = useHistory();
  const patientIds = useAtomValue(imageQualityAssessmentPatientIdsAtom);
  const setDisableKeyPress = useSetAtom(disableKeyPressAtom);

  // Local Scope Variables
  const overlayRef = useRef<HTMLDivElement>(null);
  const canGoPrevious = currentIndex !== null && currentIndex > 0;
  const canGoNext =
    currentIndex !== null && currentIndex < patientIds.length - 1;
  const isSubmitDisabled = rating === null;

  const issueTypeOptions = [
    { label: <Trans>Overexposed</Trans>, value: QualityIssueType.OVER_EXPOSED },
    {
      label: <Trans>Underexposed</Trans>,
      value: QualityIssueType.UNDER_EXPOSED,
    },
    { label: <Trans>Not sharp</Trans>, value: QualityIssueType.NOT_SHARP },
    { label: <Trans>Grainy</Trans>, value: QualityIssueType.GRAINY },
    { label: <Trans>Other</Trans>, value: QualityIssueType.OTHER },
  ];

  // Handlers
  const handlePreviousPatient = () => {
    if (currentIndex !== null && currentIndex > 0) {
      const prevPatientId = patientIds[currentIndex - 1];
      history.push(`${routes.ANNOTATOR}/${prevPatientId}`);
    }
  };

  const handleNextPatient = () => {
    if (currentIndex !== null && currentIndex < patientIds.length - 1) {
      const nextPatientId = patientIds[currentIndex + 1];
      history.push(`${routes.ANNOTATOR}/${nextPatientId}`);
    }
  };

  const handlePatientNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPatientInputValue(e.target.value);
  };

  const handlePatientNumberBlur = () => {
    const targetIndex = parseInt(patientInputValue) - 1;
    if (
      !isNaN(targetIndex) &&
      targetIndex >= 0 &&
      targetIndex < patientIds.length
    ) {
      const targetPatientId = patientIds[targetIndex];
      history.push(`${routes.ANNOTATOR}/${targetPatientId}`);
    } else if (currentIndex !== null) {
      // Reset to current if invalid
      setPatientInputValue(String(currentIndex + 1));
    }
  };

  const handlePatientNumberKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Calculate offset based on current position
    const offsetX = e.clientX - position.x;
    const offsetY = e.clientY - position.y;
    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);
  };

  const handleRatingClick = (value: number) => {
    setRating(value);
  };

  const handleIssueTypeToggle = (value: QualityIssueType) => {
    setSelectedIssueTypes((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  const handleSubmit = () => {
    if (rating) {
      onSubmit({
        rating,
        issueTypes: selectedIssueTypes,
        note,
      });
    }
  };

  const handleSubmitAndNext = () => {
    if (rating) {
      onSubmitAndNext({
        rating,
        issueTypes: selectedIssueTypes,
        note,
      });
    }
  };

  // useEffect Hooks
  // Initialize or update current index when patient changes
  useEffect(() => {
    if (currentPatientId && patientIds.length > 0) {
      const index = patientIds.indexOf(currentPatientId);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [currentPatientId, patientIds, setCurrentIndex]);

  // Disable keyboard shortcuts when overlay is closed
  useEffect(() => {
    if (!isOpen) {
      setDisableKeyPress(false);
    }
  }, [isOpen, setDisableKeyPress]);

  // Prompt user before navigating away if they have unsaved changes
  useEffect(() => {
    const hasUnsavedChanges =
      rating !== null && rating !== existingAssessment?.qualityRating;

    // Block programmatic navigation
    let unblock: (() => void) | undefined;
    if (hasUnsavedChanges && isOpen) {
      unblock = history.block(
        'You have unsaved changes to your image quality assessment. Are you sure you want to leave?',
      );
    }

    // Block browser navigation (page reload/close)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && isOpen) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      if (unblock) unblock();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [rating, existingAssessment, isOpen, history]);

  // Update state when existingAssessment changes
  useEffect(() => {
    if (existingAssessment) {
      setRating(existingAssessment.qualityRating || null);
      setSelectedIssueTypes(existingAssessment.qualityIssueTypes || []);
      setNote(existingAssessment.note || '');
    }
  }, [existingAssessment]);

  // Update input value when current index changes
  useEffect(() => {
    if (currentIndex !== null) {
      setPatientInputValue(String(currentIndex + 1));
    }
  }, [currentIndex]);

  // Handle dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, setPosition]);

  // Return Statement
  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="vai-absolute vai-z-[1000] vai-w-[30rem] vai-bg-zinc-900/95 vai-border vai-border-zinc-700 vai-rounded-lg vai-shadow-lg"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
    >
      {/* Spinner Overlay */}
      {isSubmitting && (
        <div className="vai-absolute vai-inset-0 vai-bg-zinc-900/80 vai-flex vai-items-center vai-justify-center vai-rounded-lg vai-z-10">
          <div className="vai-flex vai-flex-col vai-items-center vai-gap-2">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className="vai-text-white vai-text-2xl"
            />
            <span className="vai-text-white vai-text-sm vai-font-medium">
              <Trans>Submitting...</Trans>
            </span>
          </div>
        </div>
      )}
      {/* Draggable Header */}
      <div
        className={`vai-flex vai-items-center vai-justify-between vai-px-3 vai-py-2 vai-border-b vai-cursor-move ${
          existingAssessment?.qualityRating
            ? 'vai-bg-green-600/20 vai-border-green-500'
            : 'vai-border-zinc-700 vai-bg-zinc-800'
        }`}
        onMouseDown={handleMouseDown}
      >
        <div className="vai-flex vai-items-center vai-gap-2 vai-text-white vai-text-sm vai-font-medium">
          <FontAwesomeIcon
            icon={faGripVertical}
            className="vai-text-zinc-400"
          />
          <Trans>Quality Assessment</Trans>
          {existingAssessment?.qualityRating && (
            <span className="vai-inline-flex vai-items-center vai-px-2 vai-py-0.5 vai-rounded-full vai-bg-green-500/20 vai-text-green-400 vai-text-xs vai-font-medium vai-border vai-border-green-500/30">
              <Trans>Assessed</Trans>
            </span>
          )}
        </div>
        <div className="vai-flex vai-items-center vai-gap-2">
          {/* Patient Navigation */}
          <span className="vai-text-xs vai-text-zinc-400 vai-mr-1">
            Patient:
          </span>
          <button
            className="vai-text-zinc-400 hover:enabled:vai-text-white vai-transition-colors vai-disabled:vai-opacity-30 vai-disabled:vai-cursor-not-allowed vai-disabled:vai-pointer-events-none"
            onClick={handlePreviousPatient}
            disabled={!canGoPrevious}
            title="Previous patient"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          {currentIndex !== null && patientIds.length > 0 && (
            <div className="vai-flex vai-items-center vai-gap-1">
              <Input
                min="1"
                max={patientIds.length}
                value={patientInputValue}
                onChange={handlePatientNumberChange}
                onBlur={handlePatientNumberBlur}
                onKeyDown={handlePatientNumberKeyDown}
                className="!vai-w-12 vai-h-6 vai-text-xs vai-text-white vai-bg-zinc-800 vai-border-zinc-600 vai-text-center vai-px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="vai-text-xs vai-text-zinc-400">
                /{patientIds.length}
              </span>
            </div>
          )}
          <button
            className="vai-text-zinc-400 hover:enabled:vai-text-white vai-transition-colors vai-disabled:vai-opacity-30 vai-disabled:vai-cursor-not-allowed vai-disabled:vai-pointer-events-none"
            onClick={handleNextPatient}
            disabled={!canGoNext}
            title="Next patient"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
          <button
            className="vai-text-zinc-400 hover:vai-text-white vai-transition-colors vai-ml-2"
            onClick={onClose}
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      <div className="vai-p-4 vai-max-h-[500px] vai-overflow-y-auto">
        {/* Quality Rating */}
        <div className="vai-mb-4">
          <label
            htmlFor="quality-rating"
            className="vai-text-white vai-text-sm vai-font-medium vai-mb-1.5 vai-block"
          >
            <Trans>Rating (1-3)</Trans>
          </label>
          <div id="quality-rating" className="vai-flex vai-gap-1">
            {[1, 2, 3].map((value) => (
              <button
                key={value}
                type="button"
                className={`vai-flex-1 vai-h-8 vai-rounded vai-border vai-text-sm vai-font-semibold vai-transition-all vai-duration-200 ${
                  rating === value
                    ? 'vai-bg-blue-500 vai-border-blue-500 vai-text-white'
                    : 'vai-bg-zinc-800 vai-border-zinc-600 vai-text-white hover:vai-bg-zinc-700 hover:vai-border-zinc-500'
                }`}
                onClick={() => handleRatingClick(value)}
              >
                {value}
              </button>
            ))}
          </div>
        </div>

        {/* Quality Issue Types */}
        <div className="vai-mb-4">
          <label
            htmlFor="quality-issues"
            className="vai-text-white vai-text-sm vai-font-medium vai-mb-1.5 vai-block"
          >
            <Trans>Issues</Trans>
          </label>
          <div className="vai-flex vai-flex-wrap vai-gap-1">
            {issueTypeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`vai-px-2 vai-py-1 vai-rounded vai-border vai-text-sm vai-transition-all vai-duration-200 ${
                  selectedIssueTypes.includes(option.value)
                    ? 'vai-bg-blue-500 vai-border-blue-500 vai-text-white'
                    : 'vai-bg-zinc-800 vai-border-zinc-600 vai-text-white hover:vai-bg-zinc-700 hover:vai-border-zinc-500'
                }`}
                onClick={() => handleIssueTypeToggle(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="vai-mb-4">
          <label
            className="vai-text-white vai-text-sm vai-font-medium vai-mb-1.5 vai-block"
            htmlFor="quality-note"
          >
            <Trans>Notes</Trans>
          </label>
          <Textarea
            id="quality-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onFocus={() => setDisableKeyPress(true)}
            onBlur={() => setDisableKeyPress(false)}
            placeholder={t`Comments...`}
            className="vai-bg-zinc-800 vai-text-white vai-border-zinc-700 vai-text-xs"
            rows={3}
          />
        </div>

        {/* Action Buttons */}
        <div className="vai-flex vai-flex-col vai-gap-2">
          <div className="vai-flex vai-gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="vai-flex-1 vai-text-sm vai-h-8"
              onClick={onClose}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitDisabled || isSubmitting}
              className="vai-flex-1 vai-text-sm vai-h-8"
            >
              {isSubmitting ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <Trans>Submit</Trans>
              )}
            </Button>
          </div>
          <Button
            onClick={handleSubmitAndNext}
            disabled={isSubmitDisabled || !hasNextImage || isSubmitting}
            className="vai-w-full vai-text-sm vai-h-8"
          >
            {isSubmitting ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <Trans>Submit and Go To Next Image</Trans>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
